// ===============================
// src/ListeGardeValideeMedecin.jsx
// ===============================
// Wrapper Médecin : lecture seule + retours médecin
// Supporte tous les types : PR2, PR1, ASTREINTE_THORACIQUE, OPH via ?type=
// ===============================

import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import ListeGardeValideeCore from "./ListeGardeValideeCore.jsx";
import LogoutButton from "./components/LogoutButton.jsx";

export default function ListeGardeValideeMedecin() {
  const [searchParams] = useSearchParams();
  const typeGarde = searchParams.get("type") || "PR2";
  const isTho = typeGarde === "ASTREINTE_THORACIQUE";

  const [loadingCfg, setLoadingCfg] = useState(true);
  const [cfgError, setCfgError] = useState(null);

  const [moisOuverts, setMoisOuverts] = useState([]);
  const [selectedMois, setSelectedMois] = useState(null);

  const styles = useMemo(
    () => ({
      wrap: { maxWidth: 1100, margin: "0 auto", padding: "16px 16px 0" },
      bar: {
        display: "flex",
        gap: 12,
        alignItems: "center",
        flexWrap: "wrap",
        padding: "10px 12px",
        border: "1px solid #e5e7eb",
        borderRadius: 12,
        background: "#fff",
        marginBottom: 12,
      },
      label: { fontSize: 13, fontWeight: 800, color: "#111827" },
      select: {
        padding: "8px 10px",
        borderRadius: 10,
        border: "1px solid #d1d5db",
        fontSize: 14,
        background: "#fff",
      },
      hint: { fontSize: 12, color: "#6b7280" },
      err: { fontSize: 12, fontWeight: 800, color: "#b91c1c" },
    }),
    []
  );

  // ✅ Endpoint de config adapté au type de garde
  const configEndpoint = useMemo(() => {
    switch (typeGarde) {
      case "PR1": return "/api/v2/config/desiderata-pr1";
      case "ASTREINTE_THORACIQUE": return "/api/v2/config/desiderata-astreinte-thoracique";
      // PR2 par défaut
      default: return "/api/v2/config/desiderata-pr2";
    }
  }, [typeGarde]);

  useEffect(() => {
    const loadCfg = async () => {
      try {
        setLoadingCfg(true);
        setCfgError(null);

        const res = await fetch(configEndpoint);
        if (!res.ok) {
          const t = await res.text().catch(() => "");
          throw new Error(`HTTP ${res.status} lecture config. ${t}`);
        }

        const json = (await res.json()) || {};

        const defaultMois = (() => {
          const d = new Date();
          return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
        })();

        const list = Array.isArray(json.mois_ouverts) && json.mois_ouverts.length
          ? json.mois_ouverts
          : [
              {
                mois_cle: json.mois_cle || defaultMois,
                mois_label: json.mois_label || defaultMois,
              },
            ];

        // normalisation légère + tri par mois_cle
        const normalized = list
          .map((m) => ({
            mois_cle: String(m.mois_cle || "").trim(),
            mois_label: String(m.mois_label || m.mois_cle || "").trim(),
          }))
          .filter((m) => /^\d{4}-\d{2}$/.test(m.mois_cle))
          .sort((a, b) => a.mois_cle.localeCompare(b.mois_cle));

        setMoisOuverts(normalized);

        // par défaut : premier mois ouvert
        const defMois = normalized[0]?.mois_cle || defaultMois;
        setSelectedMois((prev) => prev || defMois);
      } catch (e) {
        console.error(e);
        setCfgError(e.message || "Erreur inconnue");
        const defMois = (() => {
          const d = new Date();
          return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
        })();
        setMoisOuverts([{ mois_cle: defMois, mois_label: defMois }]);
        setSelectedMois((prev) => prev || defMois);
      } finally {
        setLoadingCfg(false);
      }
    };

    loadCfg();
  }, [configEndpoint]);

  const selectedLabel =
    moisOuverts.find((m) => m.mois_cle === selectedMois)?.mois_label ||
    (selectedMois || "—");

  // ✅ Labels adaptés au type
  const typeLabel = isTho ? "Astreinte Thoracique" : typeGarde === "PR1" ? "Port Royal 1" : "Port Royal 2";
  const backPath = isTho ? "/desiderata-astreinte-thoracique" : typeGarde === "PR1" ? "/interface-medecins-pr1" : "/interface-medecins";
  const backLabel = isTho ? "← Retour aux desiderata d'astreinte" : "← Retour interface Médecins";

  return (
    <div>
      <div style={styles.wrap}>
        <div style={styles.bar}>
          {moisOuverts.length > 1 && (
            <>
              <div style={styles.label}>Mois</div>
              <select
                style={styles.select}
                value={selectedMois || ""}
                onChange={(e) => setSelectedMois(e.target.value)}
                disabled={loadingCfg}
              >
                {moisOuverts.map((m) => (
                  <option key={m.mois_cle} value={m.mois_cle}>
                    {m.mois_label} ({m.mois_cle})
                  </option>
                ))}
              </select>
              <div style={styles.hint}>
                Affichage en lecture seule ({isTho ? "liste d'astreintes validée" : "liste validée"} par le responsable).
              </div>
              {cfgError && <div style={styles.err}>⚠️ {cfgError}</div>}
            </>
          )}
          <div style={{ marginLeft: "auto" }}>
            <LogoutButton />
          </div>
        </div>
      </div>

      <ListeGardeValideeCore
        variant="medecin"
        topBackPath={backPath}
        topBackLabel={backLabel}
        typeGarde={typeGarde}
        mois={selectedMois}
        title={isTho
          ? `Liste d'astreintes validée ${typeLabel} (${selectedLabel})`
          : `Liste de garde validée (${selectedLabel})`
        }
        subtitle={isTho
          ? "Liste d'astreintes thoraciques validée par le responsable (lecture seule)."
          : "Liste de garde validée par le responsable (lecture seule)."
        }
      />
    </div>
  );
}
